// Program Settings screens. Pure: data in, HTML out.
// Words: PROGRAM_SETTINGS_WORDS.md. Mockup: MOCKUP_PROGRAM_SETTINGS_APPROVED.html (approved 2026-10-01).
// Glove gate retired 2026-10-05: no Gloves section. Phone and wide (styles.css).
// Every change goes through server code (functions/src/settings.ts), confirmed and logged.

import type { SettingsData } from "./firebase";
import {
  EEWP_REQUIRED,
  EEWP_SWITCHES,
  LIMITS,
  LOTO_SWITCHES,
  PROGRAM_USED_FOR,
  eewpLine,
  fmt,
  limitsLine,
  sortProgramContacts,
  type LimitDef,
  type ProgramContact,
  type ProgramContactInput,
  type ProgramSettings,
  type SwitchDef,
} from "./settings";
import { MAX_APPROVERS, NO_APPROVERS, mustApprove } from "./eewp";
import { esc, ident } from "./training-screens";
import { driveMenuLine } from "./drive-screens";
import { printerMenuLine } from "./printer-setup";

function row(label: string, value: string, end = ""): string {
  return `<div class="r"><div class="k">${esc(label)}</div><div class="v">${value}</div><div class="e">${end}</div></div>`;
}

function sheet(title: string, body: string): string {
  return `<div class="shade"><div class="sheet" role="dialog" aria-modal="true" aria-label="${esc(title)}">
    <div class="big">${esc(title)}</div>${body}</div></div>`;
}

const BACK = '<a class="back" href="#/settings">Program Settings</a>';

function changedLine(s: ProgramSettings, field: string): string {
  const c = s.changes?.[field];
  if (!c) return "Set from Config · not changed";
  const [y, m, d] = c.at.slice(0, 10).split("-");
  return `Changed by ${c.byName} · ${m}/${d}/${y}`;
}

// ---- 1. The menu ----

export function settingsMenu(d: SettingsData, flash = ""): string {
  const s = d.settings;
  const active = d.programContacts.filter((c) => c.isActive).length;
  const short = d.eewpSites.filter((x) => x.short).length;
  const r = (href: string, code: string, title: string, line: string) =>
    `<a class="menu-row" href="#/settings/${href}"><span class="num">${code}</span><span class="t"><b>${esc(title)}</b><small>${esc(line)}</small></span><span class="chev">›</span></a>`;
  return `${ident("PS", "Program Settings", "One page for the whole program")}
    ${flash}
    <div class="box">
      ${r("program", "PR", "Program", `${s.orgName} · ${s.programContactEmail} · ${(s.companyEmailDomains ?? []).length ? `${(s.companyEmailDomains ?? []).length} company ${(s.companyEmailDomains ?? []).length === 1 ? "domain" : "domains"}` : "no company domains"}`)}
      ${r("contacts", "PC", "Program Contacts", `${active} active`)}
      ${r("eewp", "EW", "EEWP", `${eewpLine(s)}${short ? ` · ${short} ${short === 1 ? "site" : "sites"} with no approvers` : ""}`)}
      ${r("loto", "LO", "LOTO", `Awareness email ${s.lotoAwarenessEmail ? "on" : "off"} · ${d.lotoVerifierCount} site LOTO ${d.lotoVerifierCount === 1 ? "verifier" : "verifiers"}`)}
      ${r("limits", "SL", "Safety Limits", limitsLine(s))}
      ${r("drive", "GD", "Google Drive", driveMenuLine(d.drive ?? null))}
      ${r("printer", "PT", "Printer Setup", printerMenuLine())}
    </div>`;
}

// ---- 2. Program ----

export interface ProgramEntry {
  orgName: string;
  programContactEmail: string;
  /** Piece 6: one per line or comma separated. */
  companyEmailDomains: string;
}

export function programForm(s: ProgramSettings, entry: ProgramEntry | null, busy: boolean, error = ""): string {
  const v = entry ?? { orgName: s.orgName, programContactEmail: s.programContactEmail, companyEmailDomains: (s.companyEmailDomains ?? []).join(", ") };
  return `${BACK}
    ${ident("PR", "Program", "Shows on every scan page")}
    <form class="box" id="program-form" novalidate>
      <div class="rows">
        <label class="r"><span class="k">Organization Name</span><span class="v"><input class="fin wide" name="orgName" value="${esc(v.orgName)}" maxlength="80" required></span><span class="e"></span></label>
        <label class="r"><span class="k">Program Contact Email</span><span class="v"><input class="fin wide" type="email" name="programContactEmail" value="${esc(v.programContactEmail)}" required></span><span class="e"></span></label>
        <label class="r"><span class="k">Company Email Domains</span><span class="v"><textarea class="fin wide" name="companyEmailDomains" rows="2" placeholder="alltech.com, ridley.com" autocapitalize="off" spellcheck="false">${esc(v.companyEmailDomains)}</textarea><span class="fhint">Contractor Access: the sponsor's email must end in one of these. Comma or one per line.</span></span><span class="e"></span></label>
      </div>
    </form>
    <div class="note">Both show on the scan page, under the asset profile. This is who a QEW or contractor reaches with a question. Edits take effect on the next scan.</div>
    ${error ? `<div class="note red">${esc(error)}</div>` : ""}
    <button class="prim" type="submit" form="program-form"${busy ? " disabled" : ""}>${busy ? "Saving…" : "Save"}</button>`;
}

// ---- 3 and 4. Program Contacts ----

export function programContactsList(list: ProgramContact[], flash = ""): string {
  const rows = sortProgramContacts(list)
    .map(
      (c) => `<a class="ct-row${c.isActive ? "" : " dim"}" href="#/settings/contacts/${esc(c.contactId)}">
        <span class="t"><b>${esc(c.name || c.email)}</b><small>${esc(c.email)}</small>
          <span class="tags">${PROGRAM_USED_FOR.map((u) => `<span class="tg${c.usedFor?.[u.key] ? "" : " off"}">${esc(u.label)}</span>`).join("")}</span></span>
        <span class="pill ${c.isActive ? "green" : "grey"}">${c.isActive ? "ACTIVE" : "OFF"}</span></a>`,
    )
    .join("");
  return `${BACK}
    ${ident("PC", "Program Contacts", `${list.filter((c) => c.isActive).length} active`)}
    ${flash}
    <div class="box">
      <div class="boxhd"><div class="nm"><b>Program Contacts</b><small>Name, email, and what they get</small></div><a class="chg" href="#/settings/contacts/new">Add Contact</a></div>
      ${rows || '<div class="empty">No program contacts yet.</div>'}
    </div>
    <div class="note green">Program contacts get every site's alerts. Site Contacts add the people for one site on top.</div>`;
}

export function programContactForm(c: ProgramContact | null, entry: ProgramContactInput | null, busy: boolean, error = ""): string {
  const v: ProgramContactInput = entry ?? {
    name: c?.name ?? "",
    email: c?.email ?? "",
    isActive: c?.isActive ?? true,
    usedFor: { jspAlerts: false, trainingAlerts: false, observationAlerts: false, eewpObserver: false, ...(c?.usedFor ?? {}) },
  };
  const ticks = PROGRAM_USED_FOR.map(
    (u) => `<label class="tick"><input type="checkbox" name="${u.key}"${v.usedFor[u.key] ? " checked" : ""}><span class="t"><b>${esc(u.label)}</b><small>${esc(u.hint)}</small></span></label>`,
  ).join("");
  return `<a class="back" href="#/settings/contacts">Cancel</a>
    ${ident("PC", c ? c.name || c.email : "Add Contact", "Program Contacts")}
    <form class="box" id="program-contact-form" data-contact="${esc(c?.contactId ?? "")}" novalidate>
      <div class="boxhd"><div class="nm"><b>Contact</b>${c ? `<small class="mono">${esc(c.contactId)}</small>` : ""}</div></div>
      <div class="rows">
        <label class="r"><span class="k">Name</span><span class="v"><input class="fin wide" name="name" value="${esc(v.name)}" placeholder="Full name" maxlength="80" autocomplete="off" required></span><span class="e"></span></label>
        <label class="r"><span class="k">Email</span><span class="v"><input class="fin wide" type="email" name="email" value="${esc(v.email)}" placeholder="name@alltech.com" autocomplete="off" required></span><span class="e"></span></label>
        ${
          c
            ? `<label class="r"><span class="k">Active</span><span class="v"><span class="tick inline"><input type="checkbox" name="isActive"${v.isActive ? " checked" : ""}><span class="t"><b>Active</b><small>Off stops every alert. Their name stays on past records.</small></span></span></span><span class="e"></span></label>`
            : ""
        }
      </div>
      <div class="boxhd sub"><div class="nm"><b>Used For</b><small>No role here. Roles matter at the site, where EEWP routes by them.</small></div></div>
      <div class="ticks">${ticks}</div>
    </form>
    ${error ? `<div class="note red">${esc(error)}</div>` : ""}
    <button class="prim" type="submit" form="program-contact-form"${busy ? " disabled" : ""}>${busy ? "Saving…" : "Save"}</button>`;
}

// ---- 5 and 9. Switches ----

function switchRows(s: ProgramSettings, defs: SwitchDef[]): string {
  return defs
    .map((d) => {
      const on = s[d.field];
      return row(
        d.label,
        `<span class="pill ${on ? "green" : "grey"}">${on ? "ON" : "OFF"}</span> <small class="muted">${esc(d.hint)}</small><small class="chg-line">${esc(changedLine(s, d.field))}</small>`,
        `<button class="chg" type="button" data-act="setting-switch" data-field="${d.field}">${on ? "Turn Off" : "Turn On"}</button>`,
      );
    })
    .join("");
}

export function eewpPage(d: SettingsData, flash = ""): string {
  const s = d.settings;
  const sites = d.eewpSites
    .map((r) => {
      const qew = r.qewTicked.length ? `<small class="chg-line">Not counted, an Active QEW: ${esc(r.qewTicked.join(", "))}</small>` : "";
      const aware = `<small class="chg-line">${esc(r.awareness.length ? `Awareness: ${r.awareness.join(", ")}` : "Awareness: none")}</small>`;
      const main = r.short ? '<span class="pill red">No Approvers</span> QEWs here see the no-approvers line.' : esc(`${mustApprove(r.approvers.length)} · ${r.approvers.join(", ")}`);
      return row(`${r.siteNumber} · ${r.name}`, `${main}${qew}${aware}`, "");
    })
    .join("");
  return `${BACK}
    ${ident("EW", "EEWP", "Energized Electrical Work Permit")}
    ${flash}
    <div class="box"><div class="boxhd"><div class="nm"><b>Energized Electrical Work Permit</b></div></div>
      <div class="rows">${switchRows(s, EEWP_SWITCHES)}</div></div>
    <div class="box"><div class="boxhd"><div class="nm"><b>Require an EEWP For</b><small>These add a requirement. They never remove one.</small></div></div>
      <div class="rows">${switchRows(s, EEWP_REQUIRED)}</div></div>
    <div class="box"><div class="boxhd"><div class="nm"><b>Approvers By Site</b><small>Every ticked approver must say yes. Up to ${MAX_APPROVERS} per site. Supervisors or managers, never a QEW.</small></div></div>
      <div class="rows">${sites || '<div class="empty">No active sites.</div>'}</div></div>
    <div class="note">Tick EEWP Approver and EEWP Awareness in each site's Site Contacts. Open the site from <a href="#/">Home</a>. A site with no approvers shows red. Its QEWs see <i>${esc(NO_APPROVERS)}</i> The approvers are frozen on each permit when it is asked.</div>`;
}

export function lotoPage(d: SettingsData, flash = ""): string {
  return `${BACK}
    ${ident("LO", "LOTO", "Program switches. People are set per site.")}
    ${flash}
    <div class="box"><div class="boxhd"><div class="nm"><b>LOTO</b></div></div>
      <div class="rows">${switchRows(d.settings, LOTO_SWITCHES)}
        ${row("Awareness contacts", `${d.lotoAwarenessCount} across the sites`, '<a class="chg" href="#/">Home</a>')}
        ${row("LOTO verifiers", `${d.lotoVerifierCount} across the sites`, '<a class="chg" href="#/">Home</a>')}
      </div></div>
    <div class="note">Who gets the awareness email, and who confirms the LOTO dates QEWs report, are set per site with the LOTO Awareness and LOTO Verifier ticks in Site Contacts. The ESP Manager also verifies from People &amp; Badges.</div>`;
}

export function switchSheet(def: SwitchDef, isOn: boolean, busy: boolean, error = ""): string {
  return sheet(
    `Turn ${isOn ? "on" : "off"} ${def.label}?`,
    `<p>${esc(isOn ? def.onLine : def.offLine)} Logged with your name and the time.</p>
    ${error ? `<div class="note red">${esc(error)}</div>` : ""}
    <button class="go" type="button" data-act="setting-switch-confirm"${busy ? " disabled" : ""}>${busy ? "Saving…" : isOn ? "Turn On" : "Turn Off"}</button>
    <button class="go2" type="button" data-act="sheet-cancel">Cancel</button>`,
  );
}

// ---- 6 to 8. Safety limits ----

export function limitsPage(s: ProgramSettings, flash = ""): string {
  const rows = LIMITS.map((l) =>
    row(
      l.label,
      `<span class="num-chip">${fmt(s[l.field])} ${esc(l.unit)}</span> <small class="muted">${esc(l.rule)}</small><small class="chg-line">${esc(changedLine(s, l.field))}</small>`,
      `<a class="chg" href="#/settings/limits/${l.field}">Change</a>`,
    ),
  ).join("");
  return `${BACK}
    ${ident("SL", "Safety Limits", "These feed the risk engine and the JSP flow")}
    ${flash}
    <div class="box"><div class="boxhd"><div class="nm"><b>Safety Limits</b><small>Confirm to change. Every change is logged.</small></div></div>
      <div class="rows">${rows}</div></div>`;
}

export function limitForm(def: LimitDef, s: ProgramSettings, entry: string | null, error = ""): string {
  const v = entry ?? String(s[def.field]);
  return `<a class="back" href="#/settings/limits">Cancel</a>
    ${ident("SL", def.label, def.rule)}
    <form class="box" id="limit-form" data-field="${def.field}" novalidate>
      <div class="rows">
        ${row("Now", `<span class="num-chip">${fmt(s[def.field])} ${esc(def.unit)}</span>`)}
        <label class="r"><span class="k">New value</span><span class="v"><span class="big-in"><input class="fin num big" name="value" inputmode="decimal" value="${esc(v)}" autocomplete="off"> <small>${esc(def.unit)}</small></span>
          <small class="fhint" id="limit-check">${esc(error || `Between ${fmt(def.min)} and ${fmt(def.max)}.`)}</small></span><span class="e"></span></label>
      </div>
    </form>
    ${error ? `<div class="note red">${esc(error)}</div>` : ""}
    <button class="prim" type="submit" form="limit-form">Save</button>`;
}

export function limitSheet(def: LimitDef, from: number, to: number, busy: boolean, error = ""): string {
  return sheet(
    `Change ${def.label} to ${fmt(to)}?`,
    `<p>${esc(def.effect(from, to))} Logged with your name and the time.</p>
    ${error ? `<div class="note red">${esc(error)}</div>` : ""}
    <button class="go" type="button" data-act="limit-confirm"${busy ? " disabled" : ""}>${busy ? "Saving…" : `Change ${def.label}`}</button>
    <button class="go2" type="button" data-act="sheet-cancel">Cancel</button>`,
  );
}
